import { CheckIcon, CloseIcon, PlusIcon } from '@/components/icons';
import { Button } from '@/components/ui';
import { cx } from '@/utils/cx';
import { formatINR } from '@/utils/format';
import { MAX_BILLS, sheetLabel, type BillForm } from '../useBillForm';
import { CustomerNameField } from '@/features/customers/components/CustomerNameField';
import { PartInput } from './PartInput';
import styles from '@/styles/components/BillSplit/style.scss';

/**
 * After Split: one chip per bill (its name and total; a red dot while something is missing on it, a tick on step 2
 * once it has been checked), ＋ for another
 * bill, ✕ to take one out. Tapping a chip shows that bill in the modal.
 */
export function BillSplitBar({ form }: { form: BillForm }) {
  const { sheets, active, allTotals } = form;
  const busy = form.editingTab !== null || form.submitting || form.done;

  return (
    <div className={styles.bar} role="tablist" aria-label="Bills">
      {sheets.map((sh, i) => (
        <div key={i} className={cx(styles.chip, i === active && styles.active)}>
          <button
            type="button"
            role="tab"
            aria-selected={i === active}
            className={styles.select}
            onClick={() => form.setActive(i)}
            disabled={busy && i !== active}
            title={allTotals[i]!.blocker ?? undefined}
          >
            {allTotals[i]!.blocker ? (
              <span className={styles.dot} aria-label="Something missing" />
            ) : form.step === 'payment' && form.reviewed[i] ? (
              <CheckIcon size={13} className={styles.seen} aria-label="Checked" />
            ) : null}
            <span className={styles.label}>{sheetLabel(sh, i)}</span>
            <span className={styles.money}>{formatINR(allTotals[i]!.total)}</span>
          </button>
          <button
            type="button"
            className={styles.remove}
            aria-label={`Take ${sheetLabel(sh, i)} out`}
            title="Take this bill out (its part goes back to the first bill)"
            onClick={() => form.removeSheet(i)}
            disabled={busy}
          >
            <CloseIcon size={12} />
          </button>
        </div>
      ))}
      {sheets.length < MAX_BILLS && (
        <Button size="sm" variant="ghost" icon={<PlusIcon size={14} />} onClick={form.addSheet} disabled={busy}>
          Bill
        </Button>
      )}
    </div>
  );
}

/**
 * Step 1 after Split, above the bookings: the shown bill's customer (an existing one, or a typed name; it names the
 * bill, and step 2 opens with them selected) and what the bill pays of the table time. Typing an amount shares it
 * over the bookings by their amounts; each booking can then be adjusted in the table.
 */
export function BillSplitHead({ form }: { form: BillForm }) {
  const tableTime = form.bookings.reduce((a, b) => a + (b.amount ?? 0), 0);
  const label = sheetLabel(form.sheets[form.active]!, form.active);

  return (
    <div className={styles.head}>
      {/* Keyed by the bill shown: switching bills starts the field (and its suggestions) afresh. */}
      <div className={styles.customer} key={form.active}>
        <CustomerNameField
          size="md"
          label="Customer"
          placeholder="Search or type a name"
          value={form.sheetName}
          customerId={form.customer?.id ?? null}
          onChange={form.setSheetCustomer}
        />
      </div>
      <div className={styles.pays}>
        <span className={styles.paysLabel}>{label} pays</span>
        <PartInput
          value={form.totals.bookingsTotal}
          max={tableTime}
          onChange={form.setSheetTotal}
          aria-label={`Table time ${label} pays`}
          className={styles.paysInput}
        />
        <span className={styles.of}>of {formatINR(tableTime)} table time</span>
        <Button size="sm" variant="ghost" onClick={form.resetEqual} title="Share every booking equally between the bills">
          Equal
        </Button>
      </div>
    </div>
  );
}
